import React, {useEffect, useState} from 'react';
import {Button, Card, Col, Form, Input, Modal, Radio, Row, Select, Space, Tooltip} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import useHTTP from '@/hooks/useHTTP.mjs';
import runAsync from '@/script/runAsync.mjs';
import Permission from '@/components/Permission.jsx';
import {useOpenModal} from '@/components/Modal/index.mjs';
import SelectIsrfDialog from '@/pages/MainPage/dialogs/SelectIsrfDialog';
import Table from '@/biz/Table.jsx';
import BizNodeTypeTag, {
    convertToBizNodeTypeName,
    convertToDppSubTypeName,
    bizNodeTypeOptions
} from "@/components/BizNodeTypeTag/BizNodeTypeTag";
import SelectIsrfReqDialog from '@/pages/MainPage/dialogs/SelectIsrfReqDialog';
import SelectPatternDlg from '@/pages/MainPage/dialogs/SelectPatternDlg';


/**
 * (迭代版)预成品，加入软件SPEC单
 * @param param
 *  modules: [{moduleType, moduleId, moduleName}]
 * @param onOk
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const Pane = ({
                  param = {
                      prjPatternId,
                      prjId,
                      sVer,
                      modules: [],
                  }, onOk, ...props
              }) => {
    const http = useHTTP();
    const openModal = useOpenModal();

    // 表单
    const [form] = Form.useForm();
    const formIsrfId = Form.useWatch('isrfId', form);

    const [genType, setGenType] = useState('OLD'); // 生单方式

    // 数据
    const [prj, setPrj] = useState({});
    const [pattern, setPattern] = useState(null);
    const [isrf, setIsrf] = useState({});
    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    // 加载显示
    const [loading, setLoading] = useState(false);

    // 组件初始化
    useEffect(() => {
        init()
    }, []);

    const init = async () => {
        form.resetFields();
        form.setFieldsValue({genType});

        doQueryPrj({prjId: param.prjId})

        doQueryPattern(param.prjPatternId);

        const isrfData = await doQueryIsrf()
        if (isrfData) {
            form.setFieldsValue({isrfName: isrfData.isrfName, isrfId: isrfData.pkid})
        }



        const modules = param.modules;
        if (modules && modules.length > 0) {
            for (var item of modules) {
                item.pkid = item.moduleId;
                item.reqType = 'ADD';
            }
        }

        setRows([...modules])
    }

    const doQueryPrj = async ({prjId}) => {
        const data = await runAsync(
            async () => {
                return await http.get(`/prjs/${prjId}`);
            }, {
                error: {content: (err) => `查询项目失败: ${err.message}`},
                loading: {show: false},
                success: {show: false},
            });

        setPrj({...data});
    }

    const doQueryPattern = async (prjPatternId) => {
        if(!prjPatternId){
            return
        }

        const data = await runAsync(
            async () => {
                return await http.get(`/prjpatterns/${prjPatternId}`);
            }, {
                error: { content: (err) => `查询失败: ${err.message}` },
                loading: { show: false },
                success: { show: false },
            });
        setPattern(data);
    }

    const doQueryIsrf = async () => {
        const data = await runAsync(
            async () => {
                return await http.get(`/isrf/listIsrf`, {
                    prjId: param.prjId,
                    stsCodes: ['DRAFT', 'RLS', 'REVISE'],
                    prjPatternId: param.prjPatternId,
                    freezeFlag: false
                });
            }, {
                error: {show: false},
                loading: {show: false},
                success: {show: false},
            });
        if (data && data.length == 1) {
            setIsrf({...data[0]});
            return data[0]
        }
        return null;
    }

    const rowSelection = {
        selectedRowKeys: selectedRows.map(({pkid}) => pkid),
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRows(selectedRows);
        },
    }

    const genTypeOptions = [
        {
            label: '新建软件SPEC单',
            value: 'NEW',
        },
        {
            label: '加入已有软件SPEC单',
            value: 'OLD',
        },
    ]

    // 生单方式变更
    const onChangeGenType = async (e) => {
        const {value, checked} = e.target
        setGenType(value)

        if (value) {
            // 清空软件SPEC单
            form.setFieldsValue({isrfName: '', isrfId: '', tarPrjPatternId: null, tarPatternName: null })
        }

        if (value == 'OLD') {
            if (isrf) {
                form.setFieldsValue({isrfName: isrf.isrfName, isrfId: isrf.pkid})
            }
        }
        else if(value == 'NEW'){
            if (pattern) {
                const { pkid, patternTitle, patternRev, sVer } = pattern;

                const rev = sVer + '.' + patternRev;
                const patternName = patternTitle ? '[' + rev + ']' + patternTitle : rev;
                form.setFieldsValue({ tarPrjPatternId: pkid, tarPatternName: patternName });
            }
        }
    }

    // 打开选择软件SPEC单对话框
    const openIsrf = async () => {
        const result = await openModal(<SelectIsrfDialog
            params={{
                prjId: param.prjId,
                stsCodes: ['DRAFT', 'RLS', 'REVISE'],
            }}/>);

        if (result) {
            const {isrfName, pkid: isrfId} = result;
            form.setFieldsValue({isrfName, isrfId})

            // 清空子表增量需求
            if (rows.length > 0) {
                for (let i = 0; i < rows.length; i++) {
                    const {isrfId: reqIsrfId} = rows[i];
                    if (reqIsrfId != null && reqIsrfId != isrfId) {
                        rows[i].isrfId = null;
                        rows[i].isrfReqId = null;
                        rows[i].isrfReqTitle = null;
                    }
                }
                setRows([...rows])
            }
        }
    }

    // 新建软件SPEC单
    const doSave = async () => {
        // if (rows.length > 0) {
        //     for (let i = 0; i < rows.length; i++) {
        //         const {isrfReqId} = rows[i];
        //         if (!isrfReqId) {
        //             Modal.error({title: '提示信息', content: '增量需求不能为空！'});
        //             return;
        //         }
        //     }
        // }
        try {
            setLoading(true);

            const formField = await form.validateFields();
            formField.pkid = formField.isrfId;

            const data = await runAsync(
                async () => {
                    return await http.post(`/isrf/addToIsrf`, {
                        ...param,
                        ...formField,
                        modules: [...rows]
                    });
                }, {
                    error: {content: (err) => `生成失败: ${err.message}`},
                    loading: {show: false},
                    success: {show: true},
                });

            onOk(data);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    // 点击选中文本框中的内容
    const onClickFocus = (e) => {
        e?.target?.select();
    }

    // 选择变更
    const onInputChange = (e, record, key) => {
        const newRows = [...rows];
        const index = newRows.findIndex(r => r.pkid == record.pkid);
        newRows[index] = {...newRows[index], [key]: (e && e.target ? e.target.value : e)};

        setRows(newRows)
    }

    // 打开选择增量需求对话框
    const openIsrfReq = async (record) => {
        if (formIsrfId == null) {
            Modal.error({title: '提示信息', content: '请先选择软件SPEC单！'});
            return;
        }

        const result = await openModal(<SelectIsrfReqDialog
            params={{
                prjId: param.prjId,
                stsCodes: ['DRAFT', 'RLS', 'REVISE'],
                reqType: record.reqType,
                moduleType: record.moduleType,
                isrfId: formIsrfId
            }}/>);

        if (result) {
            const newRows = [...rows];
            const index = newRows.findIndex(r => r.pkid == record.pkid);
            newRows[index] = {
                ...newRows[index],
                ['isrfReqId']: result.pkid,
                ['isrfId']: result.isrfId,
                ['isrfReqTitle']: result.reqTitle,
            };

            setRows(newRows)
        }
    }

    const columns = [
        {
            title: '需求类型',
            dataIndex: 'reqType',
            width: 100,
            ellipsis: true,
            getValue: record => {
                switch (record.reqType) {
                    case "ADD":
                        return '新增';
                    case "UPT":
                        return '修改';
                    case "DEL":
                        return '删除';
                    default:
                        return record.reqType;
                }
            },
            filter: <Table.FilterText/>,
        },
        {
            title: '模件类型',
            dataIndex: 'moduleType',
            width: 100,
            ellipsis: true,
            getValue: record => {
                return convertToBizNodeTypeName(record.moduleType, null, prj.subTypeCode)
            },
            filter: <Table.FilterText/>,
            render: (text, record) => {
                return <BizNodeTypeTag bizNodeType={record.moduleType} subTypeCode={prj.subTypeCode}/>
            }
        },
        {
            title: '模件名称',
            dataIndex: 'moduleName',
            width: 150,
            ellipsis: true,
            filter: <Table.FilterText/>,
        },
        {
            title: '需求项',
            dataIndex: 'isrfReqTitle',
            key: 'isrfReqTitle',
            width: 170,
            ellipsis: true,
            filter: <Table.FilterText/>,
            render: (text, record, index) => {
                return <span onClick={(e) => {
                    e.stopPropagation()
                }}>
                    <Table.InputText value={text} placeholder="请选择增量需求"
                                     addonAfter={<SearchOutlined onClick={() => openIsrfReq(record)}/>}
                                     readOnly/>
                </span>
            }
        },
        {
            title: '需求描述',
            dataIndex: 'incDesc',
            width: 150,
            filter: <Table.FilterText/>,
            render: (text, record, index) => (
                <Table.InputLongText value={text} size="small"
                                     onClick={(e) => onClickFocus(e)}
                                     onChange={(e) => onInputChange(e, record, 'incDesc')}/>
            )
        },
    ];

    const doRemove = () => {
        const filterArrays = selectedRows.map(({pkid}) => pkid)
        setRows(rows.filter(({pkid}) => !filterArrays.includes(pkid)))
        setSelectedRows([])
    }

    const formCss = {
        '& .ant-input-group > .ant-input:last-child, .ant-input-group-addon:last-child': {
            border: 'none',
        },
    }

    const cardNoBottomCss = {
        '& .ant-card-body': {
            paddingBottom: '0px',
        },
    }

    const cardSmallTopCss = {
        '& .ant-card-body': {
            paddingTop: '12px',
            paddingBottom: '12px',
        },
    }

    const openPattern = async () => {
        const result = await openModal(<SelectPatternDlg prjId={param.prjId} sVer={1} />);

        if (result) {
            const { pkid, patternTitle, patternRev, sVer } = result;

            const rev = sVer + '.' + patternRev;
            const patternName = patternTitle ? '[' + rev + ']' + patternTitle : rev;
            form.setFieldsValue({ prjPatternId: pkid, patternName });
        }
    }


    const openTarPattern = async () => {
        const result = await openModal(<SelectPatternDlg prjId={param.prjId} sVer={1} />);

        if (result) {
            const { pkid, patternTitle, patternRev, sVer } = result;

            const rev = sVer + '.' + patternRev;
            const patternName = patternTitle ? '[' + rev + ']' + patternTitle : rev;
            form.setFieldsValue({ tarPrjPatternId: pkid, tarPatternName: patternName });
        }
    }

    return (
        <>
            <Modal
                title={'新版加入软件SPEC单'}
                okText="确定"
                cancelText="取消"
                width={1200}
                bodyStyle={{minHeight: '250px', maxHeight: '60vh', overflowY: 'auto'}}
                {...props}
                footer={[
                    <Space>
                        <Permission permission="ISRF_CRT" projects={[param?.prjId + 1]}>
                            <Button onClick={() => onOk()}>取消</Button>
                            <Button type="primary" onClick={() => doSave()} loading={loading}>确定</Button>
                        </Permission>
                    </Space>
                ]}
            >
                <Form form={form} layout="vertical" css={formCss}>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                name={'genType'}
                                label=""
                                rules={[{required: true, message: '请选择'}]}
                                onChange={(e) => onChangeGenType(e)}
                            >
                                <Radio.Group options={genTypeOptions}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Card title={"软件SPEC单"} css={cardNoBottomCss}>
                        {
                            genType == 'OLD' && <Row gutter={24}>
                                <Col span={12}>
                                    <Form.Item name="isrfId" hidden><Input allowClear/></Form.Item>
                                    <Form.Item
                                        name={'isrfName'}
                                        label=""
                                        rules={[{required: true, message: '请选择软件SPEC单'}]}
                                    >
                                        <Input placeholder="请选择软件SPEC单"
                                               addonAfter={<SearchOutlined onClick={() => openIsrf()}/>}
                                               readOnly/>
                                    </Form.Item>
                                </Col>
                            </Row>
                        }

                        {
                            genType == 'NEW' && <Row gutter={24}>
                                <Col span={12}>
                                    <Form.Item name="reqNo" label="业务需求单编号">
                                        <Input allowClear/>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="isrfName" label="软件SPEC单标题"
                                               rules={[{required: true, message: '请输入软件SPEC单标题'}]}>
                                        <Input allowClear/>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="prjPatternId" hidden><Input allowClear /></Form.Item>
                                    <Form.Item name="patternName" label="源基线">
                                        <Input placeholder="请选择源基线" addonAfter={<SearchOutlined onClick={openPattern} />} readOnly />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="tarPrjPatternId" hidden><Input allowClear /></Form.Item>
                                    <Form.Item name="tarPatternName" label="目标基线">
                                        <Input placeholder="请选择源基线" addonAfter={<SearchOutlined onClick={openTarPattern} />} readOnly />
                                    </Form.Item>
                                </Col>
                            </Row>
                        }
                    </Card>

                    <Card title={"需求清单"} style={{marginTop: '20px'}} css={cardSmallTopCss}>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 12,}}>
                            <Space>
                                <Button type="danger" onClick={() => doRemove()}>删除</Button>
                            </Space>
                        </div>
                        {rows?.length > 0 && <Table
                                                   size="middle"
                                                   columns={columns}
                                                   dataSource={rows}
                                                   noRowNumber={true}
                                                   rowKey={"pkid"}
                                                   rowSelection={{type: "checkbox", ...rowSelection}}
                        />}
                    </Card>
                </Form>
            </Modal>
        </>
    );
}

export default Pane;
